/* eslint-disable no-redeclare */
import * as v from "valibot";

import { isStaleBundleDetectionDisabled } from "./build-version";

export * from "./openapi-core";

type Promisable<T> = T | Promise<T>;

type OkResponse<T> =
  | { response: Response; data: T | undefined; error?: never }
  | { response: Response; error: { detail: unknown }; data?: never };

export function ok<T>(value: Promise<OkResponse<T>>): Promise<T>;
export function ok<T>(value: OkResponse<T>): T;
export function ok(value: Promisable<OkResponse<any>>): any {
  if (value instanceof Promise) {
    return value.then(ok);
  }

  // A non-2xx response can arrive with an empty or unparseable body, which
  // leaves `error` falsy (e.g. `""` from a bodyless gateway 503). Keying off
  // `response.ok` ensures every failed request throws instead of silently
  // returning `undefined` and crashing somewhere downstream.
  if (!value.response.ok || value.error) {
    throw new OkapiError(value.response, value.error, value.error?.detail);
  }

  return value.data;
}

// Shown instead of the raw validation detail when a 422 looks like it came
// from a stale bundle (see `isLikelyStaleClient`).
export const STALE_CLIENT_MESSAGE =
  "Buttondown has been updated. Please refresh the page to keep working. If this keeps happening, contact support.";

// would've named it ApiError if openapi-typescript-fetch wasn't around,
// so naming it after the forest giraffe for now.
// https://en.wikipedia.org/wiki/Okapi
export class OkapiError extends Error {
  /**
   * A 422 that rejected one or more *unknown* fields (Pydantic
   * `extra_forbidden`). Current code never sends fields the live schema
   * forbids, so this almost always means the tab is running a stale bundle
   * from before a deploy — the fix is to refresh, not to retry. The backend
   * side of this genre is api/checkers/no_recent_app_422s.py.
   */
  public readonly isLikelyStaleClient: boolean;

  constructor(
    public response: Response,
    public data: unknown,
    detail: unknown
  ) {
    super(OkapiError.parseDetail(detail));
    this.isLikelyStaleClient =
      response.status === 422 && OkapiError.rejectsUnknownField(detail);
  }

  /**
   * The message to surface to a user: a refresh prompt for stale-client 422s,
   * otherwise the parsed validation detail. (`message` keeps the raw detail
   * for logs.)
   */
  get userFacingMessage(): string {
    return this.isLikelyStaleClient && !isStaleBundleDetectionDisabled.value
      ? STALE_CLIENT_MESSAGE
      : this.message;
  }

  /**
   * Parse API error detail into a human-readable message.
   * Handles both standard errors (string) and Pydantic validation errors (array format).
   */
  private static parseDetail(detail: unknown): string {
    const result = v.safeParse(ErrorDetail, detail);
    if (result.success) {
      return result.output;
    }
    return "An unexpected error occurred.";
  }

  private static rejectsUnknownField(detail: unknown): boolean {
    const result = v.safeParse(ValidationErrors, detail);
    return (
      result.success &&
      result.output.some((error) => error.type === "extra_forbidden")
    );
  }
}

// Pydantic validation errors (422): [{ loc, msg, type }]
const ValidationErrors = v.array(
  v.object({
    loc: v.array(v.union([v.string(), v.number()])),
    msg: v.string(),
    type: v.string(),
    ctx: v.optional(v.record(v.string(), v.unknown())),
  })
);

const ErrorDetail = v.union([
  // Standard errors: { detail: "..." }
  v.string(),
  v.pipe(
    ValidationErrors,
    v.transform((errors) => errors.map((e) => e.msg).join(", "))
  ),
]);
